import { DownloadOutlined, ExclamationCircleOutlined, MoreOutlined, OrderedListOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Dropdown, Flex, Form, Input, MenuProps, Modal, notification, Row, Table, TableColumnsType, Tag, Typography } from "antd";
import { useCompleteSalesOrderV2Mutation, useGetSalesOrderReportV2Query } from "api/sales-order/sales-order-api";
import { OBJ_COLOR } from "commons/constants";
import { getColorStatusSalesOrder, StatusSalesOrder } from "commons/constants/sales-order";
import { colorInvoiceStatus, dateFormat, numberFormat, numberFormatFractionDigits, translationInvoiceStatus } from "commons/constants/util";
import dayjs from "dayjs";
import { useExportExcel } from "hooks/useExportExcel";
import useFormToggle from "hooks/useFormToggle";
import useLoading from "hooks/useLoading";
import { ReactNode, useState } from "react";
import { SalesOrderReportV2Response } from "types/price-list.type";
import { EnumInvoiceStatus } from "types/sales-order.type";
import HistoryScaleDrawer from "./HistoryScaleDrawer";

interface Params {
	salesOrderId?: number;
	farmCode?: string;
	customerId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

interface FilterForm {
	salesOrderId?: number;
	farmCode?: string;
	customerId?: string;
	orderDate: dayjs.Dayjs[];
}

interface TotalCardProps {
	title: string;
	uom: "CON" | "KG" | "VND";
	value: number;
	color: string;
}

interface ReportV2ColumnConfig {
	dataIndex: string;
	title: string;
	wch?: number;
	fixed?: "right";
	render?: (value: any, record: SalesOrderReportV2Response) => ReactNode;
	getExportValue?: (record: SalesOrderReportV2Response, index: number) => string | number | undefined;
}

const TotalCard = ({ title, uom, value, color }: TotalCardProps) => {
	return (
		<Card style={{ borderTopColor: color, borderTopWidth: 2 }}>
			<Flex gap={20} vertical justify="space-between">
				<Typography.Text type="secondary">{title}</Typography.Text>
				<Typography.Title level={3} style={{ margin: 0, alignSelf: "center", color }}>
					{numberFormatFractionDigits(value)}
				</Typography.Title>
				<Typography.Text style={{ alignSelf: "end" }} type="secondary">
					{uom}
				</Typography.Text>
			</Flex>
		</Card>
	);
};

const REPORT_V2_COLUMNS_CONFIG: ReportV2ColumnConfig[] = [
	{
		dataIndex: "key",
		title: "STT",
		wch: 8,
		getExportValue: (_, index) => index + 1,
	},
	{
		dataIndex: "id",
		title: "Mã SO",
		wch: 12,
		render: (id: number) => (
			<Typography.Text type="success" strong>
				{id}
			</Typography.Text>
		),
	},
	{
		dataIndex: "referSolomon",
		title: "Tham chiếu Solomon",
		wch: 25,
		render: (referSolomon: string) => (
			<Typography.Text type="danger" strong>
				{referSolomon || "-"}
			</Typography.Text>
		),
		getExportValue: (record) => record.referSolomon || "-",
	},
	{
		dataIndex: "invoiceDate",
		title: "Ngày hóa đơn",
		wch: 15,
		render: (invoiceDate?: string) => (invoiceDate ? dateFormat(invoiceDate) : "-"),
		getExportValue: (record) => (record.invoiceDate ? dateFormat(record.invoiceDate) : "-"),
	},
	{
		dataIndex: "invoiceNo",
		title: "Số hóa đơn",
		wch: 15,
		render: (invoiceNo?: string) => (invoiceNo ? invoiceNo : "-"),
		getExportValue: (record) => (record.invoiceNo ? record.invoiceNo : "-"),
	},
	{
		dataIndex: "billingNo",
		title: "BillingNo",
		wch: 15,
		render: (billingNo?: string) => (billingNo ? billingNo : "-"),
		getExportValue: (record) => (record.billingNo ? record.billingNo : "-"),
	},
	{
		dataIndex: "farmCode",
		title: "Mã trại",
		wch: 12,
	},
	{
		dataIndex: "farmName",
		title: "Tên trại",
		wch: 25,
	},
	{
		dataIndex: "customerId",
		title: "Mã KH",
		wch: 15,
	},
	{
		dataIndex: "customerName",
		title: "Tên KH",
		wch: 30,
	},
	{
		dataIndex: "orderDate",
		title: "Ngày tạo",
		wch: 15,
		render: (orderDate: string) => dateFormat(orderDate),
		getExportValue: (record) => dateFormat(record.orderDate),
	},
	{
		dataIndex: "shipDate",
		title: "Ngày giao",
		wch: 20,
		render: (shipDate: string) => dayjs(shipDate).format("DD/MM/YYYY HH:mm"),
		getExportValue: (record) => dayjs(record.shipDate).format("DD/MM/YYYY HH:mm"),
	},
	{
		dataIndex: "startTimeScale",
		title: "Giờ bắt đầu cân",
		wch: 15,
		render: (startTimeScale: string) => dayjs(startTimeScale).format("DD/MM/YYYY HH:mm"),
		getExportValue: (record) => dayjs(record.startTimeScale).format("DD/MM/YYYY HH:mm"),
	},
	{
		dataIndex: "endTimeScale",
		title: "Giờ kết thúc cân",
		wch: 15,
		render: (endTimeScale: string) => dayjs(endTimeScale).format("DD/MM/YYYY HH:mm"),
		getExportValue: (record) => dayjs(record.endTimeScale).format("DD/MM/YYYY HH:mm"),
	},
	{
		dataIndex: "itemCode",
		title: "Mã sản phẩm",
		wch: 15,
	},
	{
		dataIndex: "itemName",
		title: "Tên sản phẩm",
		wch: 40,
	},
	{
		dataIndex: "chickenType",
		title: "Gà loại",
		wch: 12,
	},
	{
		dataIndex: "gender",
		title: "Giới tính",
		wch: 12,
	},
	{
		dataIndex: "orderQuantity",
		title: "Số lượng đặt",
		wch: 15,
		render: (orderQuantity: number) => numberFormat(orderQuantity),
		getExportValue: (record) => record.orderQuantity,
	},
	{
		dataIndex: "totalQuantity",
		title: "Số lượng thực xuất",
		wch: 15,
		render: (totalQuantity: number) => numberFormat(totalQuantity),
		getExportValue: (record) => record.totalQuantity,
	},
	{
		dataIndex: "totalWeightNet",
		title: "Trọng lượng",
		wch: 15,
		render: (totalWeightNet: number) => numberFormatFractionDigits(totalWeightNet),
		getExportValue: (record) => record.totalWeightNet,
	},
	{
		dataIndex: "priceListCode",
		title: "ID bảng giá",
		wch: 15,
	},
	{
		dataIndex: "currentPrice",
		title: "Đơn giá",
		wch: 15,
		render: (currentPrice) => numberFormat(currentPrice),
		getExportValue: (record) => record.currentPrice,
	},
	{
		dataIndex: "totalAmount",
		title: "Thành tiền",
		wch: 18,
		render: (totalAmount) => numberFormat(totalAmount),
		getExportValue: (record) => record.totalAmount,
	},
	{
		dataIndex: "note",
		title: "Ghi chú",
		wch: 30,
	},
	{
		dataIndex: "invoiceStatus",
		title: "Trạng thái HĐ",
		wch: 18,
		fixed: "right",
		render: (invoiceStatus) => <Tag color={colorInvoiceStatus(invoiceStatus)}>{translationInvoiceStatus(invoiceStatus)}</Tag>,
		getExportValue: (record) => translationInvoiceStatus(record.invoiceStatus as EnumInvoiceStatus),
	},
	{
		dataIndex: "status",
		title: "Trạng thái ĐH",
		wch: 18,
		fixed: "right",
		render: (status) => <Tag color={getColorStatusSalesOrder(status as StatusSalesOrder)}>{status}</Tag>,
		getExportValue: (record) => record.status,
	},
];

const ReportV2 = () => {
	const currentStartDate = dayjs().startOf("month");
	const currentEndDate = dayjs().endOf("month");

	const { isLoading } = useLoading();

	const { exportExcel } = useExportExcel();

	const { open, onOpen, onClose } = useFormToggle();

	const [salesOrderId, setSalesOrderId] = useState<number>(0);

	const [salesOrderDetailId, setSalesOrderDetailId] = useState<number>(0);

	const [isExporting, setIsExporting] = useState(false);

	const [params, setParams] = useState<Params | null>({
		startDate: currentStartDate.toISOString(),
		endDate: currentEndDate.toISOString(),
	});

	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSize] = useState(50);

	const [form] = Form.useForm();

	const { data: salesOrderReportV2Data, isFetching } = useGetSalesOrderReportV2Query({ ...params, pageNumber, pageSize }, { refetchOnMountOrArgChange: true });
	const [completeSalesOrderV2] = useCompleteSalesOrderV2Mutation();

	const handleOnFinish = (values: FilterForm) => {
		const { salesOrderId, farmCode, customerId, orderDate } = values;

		if (orderDate?.[0] && orderDate?.[1] && orderDate[0].add(6, "month").isBefore(orderDate[1])) {
			notification.error({
				message: "Khoảng thời gian Order Date không được vượt quá 6 tháng.",
			});
			return;
		}

		setParams({ salesOrderId, farmCode, customerId, startDate: orderDate?.[0].toISOString(), endDate: orderDate?.[1].toISOString() });
		setPageNumber(1);
	};

	const handleReset = () => {
		form.resetFields();
		setParams({});
		setPageNumber(1);
	};

	const handleConfirmSyncSolomon = (salesOrderId: number) => {
		const modal = Modal.confirm({
			title: "Xác nhận đồng bộ Solomon",
			icon: <ExclamationCircleOutlined />,
			content: "Bạn có chắc chắn muốn đồng bộ đơn hàng này sang Solomon không?",
			okText: "Đồng ý",
			cancelText: "Huỷ",
			maskClosable: false,
			onOk: async () => {
				modal.update((prevConfig) => ({
					...prevConfig,
					closable: false,
					keyboard: false,
					maskClosable: false,
					cancelButtonProps: {
						...prevConfig.cancelButtonProps,
						disabled: true,
					},
				}));

				try {
					await completeSalesOrderV2(salesOrderId).unwrap();
				} catch (error: any) {
					modal.update((prevConfig) => ({
						...prevConfig,
						closable: true,
						keyboard: true,
						cancelButtonProps: {
							...prevConfig.cancelButtonProps,
							disabled: false,
						},
					}));

					notification.error({
						message: error?.data?.message || "Có lỗi xảy ra khi đồng bộ Solomon.",
					});
				}
			},
		});
	};

	const renderItems = (record: SalesOrderReportV2Response, salesOrderId: number, salesOrderDetailId: number) => {
		const items: MenuProps["items"] = [
			{
				key: "1",
				label: <Typography.Text style={{ color: OBJ_COLOR.SUCCESS }}>Xem phiếu cân</Typography.Text>,
				icon: <OrderedListOutlined style={{ color: OBJ_COLOR.SUCCESS }} />,
				onClick: () => {
					setSalesOrderId(salesOrderId);
					setSalesOrderDetailId(salesOrderDetailId);
					onOpen();
				},
			},

			record.status === StatusSalesOrder.Submit
				? {
						key: "2",
						label: <Typography.Text style={{ color: OBJ_COLOR.INFO }}>Đồng bộ Solomon</Typography.Text>,
						icon: <SyncOutlined style={{ color: OBJ_COLOR.INFO }} />,
						onClick: () => handleConfirmSyncSolomon(salesOrderId),
					}
				: null,
		];

		return items;
	};

	const columns: TableColumnsType<SalesOrderReportV2Response> = [
		...REPORT_V2_COLUMNS_CONFIG.map((column) => ({
			title: column.title,
			dataIndex: column.dataIndex,
			ellipsis: true,
			fixed: column.fixed,
			render: column.render,
		})),
		{
			dataIndex: "id",
			fixed: "right",
			render: (id: number, record) => (
				<Dropdown menu={{ items: renderItems(record, id, record.salesOrderDetailId) }}>
					<MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
				</Dropdown>
			),
			ellipsis: true,
		},
	];

	const handleOnClickExport = async () => {
		try {
			setIsExporting(true);

			const Heading = [
				REPORT_V2_COLUMNS_CONFIG.reduce<Record<string, string>>((result, column) => {
					result[column.dataIndex] = column.title;
					return result;
				}, {}),
			];

			const wscols = REPORT_V2_COLUMNS_CONFIG.map((column) => ({
				wch: column.wch ?? 15,
			}));

			exportExcel({
				Heading,
				header: [],
				nameFile: `Sales_Order_Report_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`,
				wscols,
				dataExport: salesOrderReportV2Data?.items?.map((item, index) =>
					REPORT_V2_COLUMNS_CONFIG.reduce<Record<string, string | number | undefined>>((result, column) => {
						result[column.dataIndex] = column.getExportValue ? column.getExportValue(item, index) : (item[column.dataIndex as keyof SalesOrderReportV2Response] as string | number | undefined);
						return result;
					}, {})
				),
			});
		} catch (error) {
			notification.error({
				message: "Có lỗi xảy ra khi xuất file Excel.",
			});
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<>
			<Row gutter={[20, 20]}>
				<Col span={24}>
					<Form form={form} layout="vertical" onFinish={handleOnFinish}>
						<Row gutter={[10, 10]} align="bottom">
							<Col xs={12} xl={4}>
								<Form.Item label="Sales Order Id" name="salesOrderId">
									<Input placeholder="Sales Order Id" />
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item label="Farm code" name="farmCode">
									<Input placeholder="Farm code" />
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item label="Customer Id" name="customerId">
									<Input placeholder="Customer Id" />
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item label="Order Date" name="orderDate" initialValue={[currentStartDate, currentEndDate]}>
									<DatePicker.RangePicker allowClear={false} format={"DD-MM-YYYY"} style={{ width: "100%" }} />
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item>
									<Button style={{ width: "100%" }} type="primary" htmlType="submit" disabled={isLoading} loading={isLoading}>
										Search
									</Button>
								</Form.Item>
							</Col>
							<Col xs={12} xl={4}>
								<Form.Item>
									<Button onClick={handleReset} style={{ width: "100%", background: isLoading ? "#0000000a" : OBJ_COLOR.WARNING }} type="primary" disabled={isLoading}>
										Reset
									</Button>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</Col>
				<Col span={24}>
					<Row gutter={[20, 20]}>
						<Col xs={24} xl={8}>
							<TotalCard title="Tổng số lượng" value={salesOrderReportV2Data?.items?.map((item) => item.totalQuantity).reduce((a, b) => a + b, 0) || 0} uom="CON" color={OBJ_COLOR.INFO} />
						</Col>
						<Col xs={24} xl={8}>
							<TotalCard title="Tổng trọng lượng" value={salesOrderReportV2Data?.items?.map((item) => item.totalWeightNet).reduce((a, b) => a + b, 0) || 0} uom="KG" color={OBJ_COLOR.DANGER} />
						</Col>
						<Col xs={24} xl={8}>
							<TotalCard title="Tổng thành tiền" value={salesOrderReportV2Data?.items?.map((item) => item.totalAmount).reduce((a, b) => a + b, 0) || 0} uom="VND" color={OBJ_COLOR.SUCCESS} />
						</Col>
					</Row>
				</Col>
				<Col span={24}>
					<Flex gap={10}>
						<Button onClick={handleOnClickExport} type="primary" ghost icon={<DownloadOutlined />} loading={isExporting} disabled={isExporting}>
							Export
						</Button>
					</Flex>
				</Col>
				<Col span={24}>
					<Table
						loading={isLoading || isFetching}
						columns={columns}
						dataSource={salesOrderReportV2Data?.items?.map((item, key) => ({ ...item, key: key + 1 }))}
						scroll={{ x: true }}
						pagination={{
							current: pageNumber,
							pageSize,
							total: salesOrderReportV2Data?.totalCount,
							showSizeChanger: true,
							pageSizeOptions: ["50", "100", "200", "500"],
							onChange: (page, size) => {
								setPageNumber(page);
								setPageSize(size);
							},
						}}
					/>
				</Col>
			</Row>

			<HistoryScaleDrawer open={open} onClose={onClose} salesOrderId={salesOrderId} salesOrderDetailId={salesOrderDetailId} />
		</>
	);
};

export default ReportV2;
